import React, { useCallback, useEffect, useState } from 'react';
import './LandingPage.css';

const CHECKOUT_URL = 'https://pay.kiwify.com.br/qGNnjXV';
const SITE_URL = 'https://fatcat.com.br';

const FALLBACK_ROWS = [
  { ticker: 'VALE3', strategy: 'Kairi + Confluência', status: 'ESTUDAR' },
  { ticker: 'PETR4', strategy: 'Reversão na Tendência', status: 'ACOMPANHAR' },
  { ticker: 'BBAS3', strategy: 'RSI(2) Connors', status: 'PRÓXIMO' },
  { ticker: 'ITUB4', strategy: 'Análise Setorial', status: 'CONTEXTO' },
];

const STRATEGIES = [
  {
    number: '01',
    title: 'Scanner B3',
    label: 'Mapa diário de oportunidades',
    description: 'Consolida os ativos acompanhados e organiza cada leitura por estratégia, contexto e estado para começar sua análise com menos ruído.',
    boundary: 'Não substitui sua validação do ativo.',
  },
  {
    number: '02',
    title: 'Reversão na Tendência',
    label: 'Pullbacks dentro da tendência',
    description: 'Ajuda a estudar recuos de preço com filtro de tendência, ponto de entrada, stop, alvo e relação risco/retorno visíveis.',
    boundary: 'Não envia ordem nem determina uma compra.',
  },
  {
    number: '03',
    title: 'Análise Setorial',
    label: 'O ativo no lugar certo',
    description: 'Compara o movimento do ativo aos pares para separar um evento do setor de uma leitura específica da empresa.',
    boundary: 'Não elimina o risco do mercado.',
  },
  {
    number: '04',
    title: 'RSI(2) Connors',
    label: 'Contexto de reversão à média',
    description: 'Reúne RSI(2), médias, candles e filtros de tendência em uma leitura curta e consistente para o swing trader.',
    boundary: 'Não prevê o próximo pregão.',
  },
  {
    number: '05',
    title: 'Kairi + Confluência',
    label: 'Checklist de cinco condições',
    description: 'Transforma desvio de média, RSI, IBS, tendência e volume em uma checklist visual de contexto técnico.',
    boundary: 'Não é uma recomendação personalizada.',
  },
];

const FAQS = [
  {
    question: 'O FatCat executa operações na corretora?',
    answer: 'Não. O FatCat é uma plataforma de análise. As decisões, ordens e gestão de risco continuam sob responsabilidade do investidor.',
  },
  {
    question: 'Para quem a plataforma foi construída?',
    answer: 'Para quem opera swing trade na B3 e quer reunir filtros, contexto e registro da própria rotina em um único painel.',
  },
  {
    question: 'O que eu encontro no detalhe de cada ativo?',
    answer: 'Você encontra cotação, histórico de preços, indicadores técnicos, volume, tendência e contexto setorial para revisar a leitura antes de tomar uma decisão.',
  },
  {
    question: 'O que é a carteira teórica?',
    answer: 'É um ambiente para registrar e acompanhar posições de referência dentro da plataforma, sem movimentar capital ou enviar ordens ao mercado.',
  },
];

function formatScannerDate(value) {
  if (!value) return 'Amostra de interface';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Amostra de interface';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function getStatus(row) {
  const signal = String(row.signal || '').toLowerCase();
  if (signal === 'buy') return 'ESTUDAR';
  if (signal === 'investigate') return 'PRÓXIMO';
  if (signal === 'watch') return 'ACOMPANHAR';
  if (signal === 'danger') return 'CAUTELA';
  return 'CONTEXTO';
}

function useScannerPreview() {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let active = true;
    fetch('/api/scanner/hoje')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data?.assets?.length) return;
        setPreview({
          date: data.kpis?.date,
          rows: data.assets.slice(0, 4).map((asset) => ({
            ticker: asset.ticker,
            strategy: asset.strategy || 'Leitura consolidada',
            status: getStatus(asset),
          })),
        });
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  return preview;
}

function useReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal, .reveal-stagger');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
      <path d="M3 10h13M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function LandingPage({ onLogin }) {
  const scannerPreview = useScannerPreview();
  useReveal();

  const handleAdminClick = useCallback(() => {
    window.location.hash = 'admin';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  const rows = scannerPreview?.rows?.length ? scannerPreview.rows : FALLBACK_ROWS;

  return (
    <div className="page landing-page">
      <nav className="nav" aria-label="Navegação principal">
        <div className="container nav-inner">
          <a className="logo" href="#inicio" aria-label="FatCat, início">
            <img src="/fatcat-logo-full.png" alt="FatCat" className="nav-logo-img" />
          </a>
          <div className="nav-links">
            <a href="#produto">Produto</a>
            <a href="#rotina">Como funciona</a>
            <a href="#evidencia">Evidência</a>
            <a href="#preco">Preço</a>
          </div>
          <div className="nav-cta">
            <button className="btn btn-line nav-login" onClick={onLogin}>Entrar</button>
            <a className="btn btn-primary nav-subscribe" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer">
              Conhecer o FatCat
            </a>
          </div>
        </div>
      </nav>

      <main>
        <section className="hero" id="inicio">
          <div className="container hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">Plataforma de análise para swing trade na B3</span>
              <h1>Sua análise da B3 começa com um <span>plano</span>, não com uma opinião.</h1>
              <p className="lead">
                O FatCat organiza cinco visões de estratégia, contexto técnico e setorial, pesquisa, alertas e uma carteira teórica em uma rotina que você consegue repetir.
              </p>
              <div className="hero-cta">
                <a className="btn btn-primary btn-lg" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer">
                  Conhecer o FatCat <ArrowIcon />
                </a>
                <button className="btn btn-line btn-lg" onClick={onLogin}>Já sou assinante</button>
              </div>
              <p className="hero-qualifier">Plataforma de análise. O FatCat não envia ordens à corretora.</p>
              <div className="hero-proof">
                <div>
                  <strong>5</strong>
                  <span>visões de análise</span>
                </div>
                <div>
                  <strong>1</strong>
                  <span>rotina no mesmo painel</span>
                </div>
                <div>
                  <strong>B3</strong>
                  <span>foco no mercado brasileiro</span>
                </div>
              </div>
            </div>

            <div className="hero-mock reveal" aria-label="Exemplo de interface do Scanner B3">
              <div className="product-window">
                <div className="product-topbar">
                  <div className="product-brand">FATCAT <span>/ SCANNER B3</span></div>
                  <div className="product-date">{scannerPreview ? formatScannerDate(scannerPreview.date) : 'EXEMPLO DE LEITURA'}</div>
                </div>
                <div className="product-layout">
                  <aside className="product-rail" aria-hidden="true">
                    <span className="rail-mark active" />
                    <span className="rail-mark" />
                    <span className="rail-mark" />
                    <span className="rail-mark" />
                  </aside>
                  <div className="scanner-panel">
                    <div className="panel-heading">
                      <div>
                        <span className="micro-label">VISÃO CONSOLIDADA</span>
                        <strong>O que merece sua atenção</strong>
                      </div>
                      <span className="panel-count">B3</span>
                    </div>
                    <div className="scanner-columns" aria-hidden="true">
                      <span>ATIVO</span><span>ESTRATÉGIA</span><span>ESTADO</span>
                    </div>
                    {rows.map((row) => (
                      <div className="scanner-row" key={`${row.ticker}-${row.strategy}`}>
                        <strong>{row.ticker}</strong>
                        <span>{row.strategy}</span>
                        <span className={`state state-${row.status.toLowerCase().replace(' ', '-')}`}>{row.status}</span>
                      </div>
                    ))}
                    <div className="scanner-foot">
                      <span>Contexto técnico e setorial antes da decisão</span>
                      <span className="open-analysis">ver análise <ArrowIcon /></span>
                    </div>
                  </div>
                  <aside className="detail-panel">
                    <span className="micro-label">LEITURA DO ATIVO</span>
                    <strong>Checklist de contexto</strong>
                    <div className="checklist">
                      <div><i className="check-dot is-on" /> Tendência primária</div>
                      <div><i className="check-dot is-on" /> Desvio de média</div>
                      <div><i className="check-dot" /> Força do setor</div>
                      <div><i className="check-dot" /> Gestão de risco</div>
                    </div>
                    <div className="detail-note">A interface organiza critérios. A decisão continua sendo sua.</div>
                  </aside>
                </div>
                <div className="product-caption">Amostra de interface. Valores e estados ilustram como o painel é organizado.</div>
              </div>
            </div>
          </div>
        </section>

        <section className="routine-section" id="rotina">
          <div className="container">
            <div className="routine-intro reveal">
              <span className="section-eyebrow">Uma rotina, não um palpite</span>
              <h2>Transforme a próxima análise em um processo que você reconhece.</h2>
            </div>
            <div className="routine-grid reveal-stagger">
              <article className="routine-card">
                <span className="routine-time">DEPOIS DO FECHAMENTO</span>
                <strong>Consolidar</strong>
                <p>Abra a visão consolidada e veja onde cada estratégia encontrou contexto para ser estudado.</p>
              </article>
              <article className="routine-card">
                <span className="routine-time">ANTES DA ABERTURA</span>
                <strong>Filtrar</strong>
                <p>Confira tendência, setor, indicadores e confluência do ativo sem abrir uma coleção de abas.</p>
              </article>
              <article className="routine-card">
                <span className="routine-time">NA SUA DECISÃO</span>
                <strong>Registrar</strong>
                <p>Use uma tese explícita, risco definido e a carteira teórica para acompanhar o que sua regra faria.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section product-section" id="produto">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-eyebrow">Cinco visões, uma linguagem</span>
              <h2>Ferramentas diferentes para perguntas que acontecem na mesma operação.</h2>
              <p>Em vez de procurar um “sinal pronto”, você encontra camadas de contexto para decidir com método e responsabilidade.</p>
            </div>
            <div className="strategy-list reveal-stagger">
              {STRATEGIES.map((strategy) => (
                <article className="strategy-row" key={strategy.number}>
                  <span className="strategy-number">{strategy.number}</span>
                  <div>
                    <span className="strategy-label">{strategy.label}</span>
                    <h3>{strategy.title}</h3>
                  </div>
                  <p>{strategy.description}</p>
                  <span className="strategy-boundary">{strategy.boundary}</span>
                </article>
              ))}
            </div>
            <div className="tool-band reveal">
              <div className="tool-band-title">
                <SearchIcon />
                <div><span className="micro-label">FERRAMENTAS DE APOIO</span><strong>O que envolve a análise</strong></div>
              </div>
              <div className="tool-items">
                <span>Busca de ativos</span>
                <span>Contexto de mercado</span>
                <span>Alertas visuais e sonoros</span>
                <span>Detalhe técnico</span>
                <span>Carteira teórica</span>
              </div>
            </div>
          </div>
        </section>

        <section className="asset-journey">
          <div className="container asset-grid">
            <div className="asset-copy reveal">
              <span className="section-eyebrow">Antes de agir, veja o caminho</span>
              <h2>Uma leitura de ativo deixa de ser uma tela isolada.</h2>
              <p>O FatCat conecta a origem do contexto à sua revisão: estratégia, tendência, pares setoriais, checklist e acompanhamento teórico.</p>
              <a className="inline-link" href="#preco">Conhecer a plataforma <ArrowIcon /></a>
            </div>
            <div className="journey-board reveal">
              <div className="journey-ticker"><span>EXEMPLO DE FLUXO</span><strong>VALE3</strong></div>
              <div className="journey-path">
                <div><b>01</b><span>Scanner consolidado</span><small>A leitura começa por uma estratégia.</small></div>
                <div><b>02</b><span>Tendência e setor</span><small>O ativo ganha contexto antes do próximo passo.</small></div>
                <div><b>03</b><span>Checklist</span><small>Critérios visíveis, sem esconder as condições.</small></div>
                <div><b>04</b><span>Carteira teórica</span><small>Registre a referência para acompanhar sua rotina.</small></div>
              </div>
              <p>Exemplo de fluxo de análise. Não representa uma recomendação de investimento.</p>
            </div>
          </div>
        </section>

        <section className="section evidence-section" id="evidencia">
          <div className="container evidence-grid">
            <div className="evidence-copy reveal">
              <span className="section-eyebrow">Contexto antes da decisão</span>
              <h2>Uma leitura que conecta sinal, ativo e contexto.</h2>
              <p>No detalhe do ativo, você reúne histórico de preços, indicadores técnicos, volume, tendência e comparação setorial para revisar a leitura antes de registrar uma posição teórica.</p>
              <div className="evidence-note">
                <strong>Critérios visíveis no painel.</strong>
                <span>O FatCat organiza as informações para você avaliar a oportunidade com método e responsabilidade.</span>
              </div>
            </div>
            <div className="method-card reveal">
              <div className="method-head">
                <span className="micro-label">DETALHE DO ATIVO</span>
                <span>LEITURA TÉCNICA</span>
              </div>
              <div className="method-row"><span>Cotação e variação</span><strong>Atualizadas no pregão</strong></div>
              <div className="method-row"><span>Indicadores</span><strong>Kairi, RSI(2), IBS e médias</strong></div>
              <div className="method-row"><span>Contexto setorial</span><strong>Comparação entre pares</strong></div>
              <div className="method-row"><span>Alertas</span><strong>Visuais, sonoros e WhatsApp</strong></div>
              <div className="method-row"><span>Acompanhamento</span><strong>Carteira teórica</strong></div>
              <div className="method-footer">Use a leitura como parte da sua análise. A decisão, a ordem e a gestão de risco continuam sendo suas.</div>
            </div>
          </div>
        </section>

        <section className="section fit-section">
          <div className="container">
            <div className="fit-grid reveal-stagger">
              <article className="fit-card fit-card-yes">
                <span className="section-eyebrow">Para quem é</span>
                <h2>Para quem quer uma rotina de swing trade mais explícita.</h2>
                <ul>
                  <li>Você prefere critérios visíveis a seguir opiniões dispersas.</li>
                  <li>Você quer combinar técnica, setor e histórico antes de decidir.</li>
                  <li>Você busca menos alternância entre telas e mais consistência de processo.</li>
                </ul>
              </article>
              <article className="fit-card">
                <span className="section-eyebrow">Para quem não é</span>
                <h2>Não é um atalho para terceirizar sua decisão.</h2>
                <ul>
                  <li>Não executa ordens, não opera day trade automaticamente e não substitui uma corretora.</li>
                  <li>Não oferece retorno garantido ou indicação personalizada de investimento.</li>
                  <li>Não dispensa estudo, gestão de risco e responsabilidade sobre seu capital.</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="faq-section">
          <div className="container faq-grid">
            <div className="faq-intro reveal">
              <span className="section-eyebrow">Perguntas diretas</span>
              <h2>Uma plataforma clara desde o primeiro clique.</h2>
              <p>Conheça os limites e o papel do FatCat antes de entrar na sua rotina.</p>
            </div>
            <div className="faq-list reveal-stagger">
              {FAQS.map((item) => (
                <details key={item.question}>
                  <summary>{item.question}<span aria-hidden="true">+</span></summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="section pricing-section" id="preco">
          <div className="container">
            <div className="section-head reveal">
              <span className="section-eyebrow">Acesso completo</span>
              <h2>Um plano para construir uma análise mais repetível.</h2>
              <p>Acesso às cinco visões e às ferramentas que conectam sua rotina de análise.</p>
            </div>
            <div className="pricing-card reveal">
              <div className="pricing-top"><span className="plan">PLANO FATCAT</span><span className="access-label">ACESSO COMPLETO</span></div>
              <div className="price-row"><span>R$</span><strong>97</strong><em>/ mês</em></div>
              <p className="price-sub">Assinatura mensal para usar a plataforma no seu ritmo.</p>
              <ul>
                <li>Scanner consolidado e cinco visões de análise</li>
                <li>Detalhe técnico, contexto setorial e busca de ativos</li>
                 <li>Alertas visuais, sonoros e pelo WhatsApp</li>
                <li>Carteira teórica para acompanhamento</li>
              </ul>
              <div className="pricing-actions">
                <a className="btn btn-primary btn-lg" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer">Assinar o FatCat <ArrowIcon /></a>
                <span>Política de garantia e cancelamento conforme as condições do checkout.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="container">
            <span className="section-eyebrow">O próximo pregão começa antes da abertura</span>
            <h2>Monte uma rotina de análise que você consegue repetir.</h2>
            <p>Organize contexto, regra e acompanhamento em um único lugar.</p>
            <div className="cta-row">
              <a className="btn btn-primary btn-lg" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer">Conhecer o FatCat <ArrowIcon /></a>
              <button className="btn btn-line btn-lg" onClick={onLogin}>Já tenho acesso</button>
            </div>
            <small>Não é recomendação de investimento. Invista com responsabilidade e gestão de risco.</small>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="container foot-inner">
          <img src="/fatcat-logo-full.png" alt="FatCat" className="foot-logo-img" />
          <div className="foot-links">
            <a href={SITE_URL}>fatcat.com.br</a>
            <a href="mailto:contato@fatcat.app.br">contato@fatcat.app.br</a>
            <a href="https://t.me/fatcattraders" target="_blank" rel="noopener noreferrer">Contato</a>
            <a href="#preco">Assinar</a>
          </div>
          <div className="foot-meta">
            <button className="admin-pill" onClick={handleAdminClick}>Acesso administrativo</button>
            <span>© 2026 FatCat · Plataforma de análise quantitativa para a B3</span>
          </div>
        </div>
      </footer>
    </div>
  );
}